/**
 * «Συγχρονισμός τιμών από τον κατάλογο» — η στεγνή δοκιμή ΠΡΙΝ τη στεγνή δοκιμή
 * (§13.19.Ι, §11.7 `POST …/price-sync`).
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ ΞΕΧΩΡΙΣΤΟ ΠΑΡΑΘΥΡΟ: το `price-sync` δημιουργεί πρόχειρη αλλαγή
 * τιμής για ΚΑΘΕ γραμμή που διαφέρει. Στα Κουλουράδικα αυτό είναι ~40 αλλαγές σε
 * 20 καταστήματα, από τις οποίες οι περισσότερες αφορούν ~17 προϊόντα με
 * ΣΥΣΤΗΜΑΤΙΚΗ διαφορά (δηλαδή παλιά εμπορική απόφαση). Ο χειριστής πρέπει να
 * αποφασίσει ΑΝΑ ΠΡΟΪΟΝ πριν φτιαχτεί έστω μία γραμμή — αλλιώς η «διόρθωση»
 * κατεβάζει 140 τιμές που κάποιος ανέβασε επίτηδες.
 *
 * Οι αποφάσεις αποθηκεύονται ΠΡΩΤΑ (PUT …/price-overrides) και ΜΕΤΑ τρέχει ο
 * συγχρονισμός: αλλιώς ο planner θα διάβαζε τις παλιές.
 */

import React, { useMemo } from 'react';
import { AlertTriangle, Coins, Loader2, X } from 'lucide-react';
import { PriceOverride } from '../../services/platformCatalogApi';
import {
  fmtDelta, fmtMoney, PriceIndex, PriceMasterInfo, sortMastersByDelta,
} from './catalogUi';
import PriceDecision from './PriceDecision';

interface Props {
  index: PriceIndex;
  /** null = όλα τα καταστήματα· αλλιώς ο συγχρονισμός περιορίζεται σε ένα. */
  storeId: number | null;
  storeName: string | null;
  canDecide: boolean;
  busy: boolean;
  /** Μόνιμο μήνυμα από 409/403 της τελευταίας προσπάθειας. */
  error: string | null;
  draftFor: (masterKey: string) => PriceOverride;
  isDirty: (masterKey: string) => boolean;
  onDraftChange: (next: PriceOverride) => void;
  dirtyCount: number;
  onClose: () => void;
  /** Αποθηκεύει ό,τι άλλαξε και τρέχει το `price-sync`. */
  onConfirm: () => void;
}

const PriceSyncModal: React.FC<Props> = ({
  index, storeId, storeName, canDecide, busy, error,
  draftFor, isDirty, onDraftChange, dirtyCount, onClose, onConfirm,
}) => {
  /** Μόνο τα είδη με πραγματική διαφορά — τα υπόλοιπα δεν παράγουν γραμμή. */
  const masters = useMemo(() => {
    const keys = Object.keys(index.byMaster);
    const list: PriceMasterInfo[] = [];
    for (let i = 0; i < keys.length; i++) {
      const m = index.byMaster[keys[i]];
      if (m.delta === null) continue;
      list.push(m);
    }
    return sortMastersByDelta(list);
  }, [index]);

  /**
   * Η προ-μέτρηση γίνεται πάνω στις ΠΡΟΧΕΙΡΕΣ αποφάσεις, όχι στις αποθηκευμένες:
   * αλλιώς ο αριθμός που βλέπει ο χειριστής τη στιγμή που πατάει θα ήταν ο
   * αριθμός πριν τις αλλαγές του.
   */
  const preview = useMemo(() => {
    let willSend = 0;
    let kept = 0;
    let fixed = 0;
    const storeSet: Record<string, boolean> = {};
    for (let i = 0; i < masters.length; i++) {
      const m = masters[i];
      const mode = draftFor(m.master_key).mode || 'catalog';
      if (mode === 'keep_platform') { kept += 1; continue; }
      // «Σταθερή» σε είδος με μεγέθη ΔΕΝ ισχύει (§11.7): μετράει ως «κατάλογος»,
      // ακριβώς όπως τη διαβάζει ο planner και η μετάπτωση.
      if (mode === 'fixed' && !m.sized) fixed += 1;
      for (let j = 0; j < m.rows.length; j++) {
        const r = m.rows[j];
        const delta = (r.delta === null || r.delta === undefined) ? null : Number(r.delta);
        if (r.source !== 'catalog' || delta === null || Math.abs(delta) < 0.005) continue;
        willSend += 1;
        storeSet[String(r.store_id)] = true;
      }
    }
    return { willSend, kept, fixed, stores: Object.keys(storeSet).length };
  }, [masters, draftFor]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-surface-2 border border-line-strong rounded-2xl w-full max-w-4xl my-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-line">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <Coins className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Συγχρονισμός τιμών από τον κατάλογο (delivery)
            </h3>
            <p className="text-xs text-t3 mt-1">
              {storeId === null
                ? 'Όλα τα καταστήματα του οργανισμού'
                : `Μόνο: ${storeName || `κατ. ${storeId}`}`}
              {' · '}{masters.length} είδη με διαφορά
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-3 text-t3">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[68vh] overflow-y-auto">
          <div className="p-3 rounded-xl border border-line bg-surface-1/50 text-xs text-t2">
            Ο συγχρονισμός <strong>δεν στέλνει τίποτα</strong>: φτιάχνει πρόχειρες αλλαγές τιμής και ανοίγει
            τη στεγνή δοκιμή. Οι διαφορές είναι <strong>συστηματικές ανά προϊόν</strong> (ίδιες και στα 20
            καταστήματα) — 7 εμφιαλωμένα Milko/Life και ~10 sandwich με τυρί στέκονται <strong>+0,10/+0,20</strong>
            {' '}πάνω από τον κατάλογο, ενώ 3 cocktails ενός καταστήματος στέκονται <strong>−0,20</strong> κάτω.
            Ό,τι σημειώσεις «Κράτα την τιμή efood» δεν θα αγγιχτεί <strong>ποτέ</strong>, ούτε σήμερα ούτε σε
            επόμενο συγχρονισμό. Η διαθεσιμότητα <strong>δεν</strong> αλλάζει ποτέ από εδώ.
          </div>

          {error && (
            <div className="p-3 rounded-xl text-sm flex items-start gap-2 border bg-amber-50 dark:bg-amber-900/25 border-amber-300 dark:border-amber-700/50 text-amber-800 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="min-w-0">{error}</span>
            </div>
          )}

          {masters.length === 0 ? (
            <p className="text-sm text-t4 py-6 text-center">
              Καμία διαφορά τιμής: δεν υπάρχει τίποτα να συγχρονιστεί.
            </p>
          ) : (
            <div className="border border-line rounded-xl divide-y divide-line">
              {masters.map(m => {
                const draft = draftFor(m.master_key);
                return (
                  <div key={m.master_key} className="p-3 flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-t1 font-medium">{m.name}</div>
                      <div className="text-[11px] text-t4">
                        {m.category_label || '—'} · διαφορά{' '}
                        <span className="text-amber-700 dark:text-amber-300 font-semibold">{fmtDelta(m.delta)}</span>
                        {' '}σε <strong>{m.stores}</strong> {m.stores === 1 ? 'κατάστημα' : 'καταστήματα'}
                        {m.catalog_min !== null && (
                          <> · κατάλογος {fmtMoney(m.catalog_min)}
                            {m.catalog_max !== null && m.catalog_max !== m.catalog_min
                              ? ` – ${fmtMoney(m.catalog_max)}` : ''}</>
                        )}
                      </div>
                      {m.note && <div className="text-[11px] text-t3 mt-0.5">Σημείωση: {m.note}</div>}
                    </div>
                    <PriceDecision
                      value={draft}
                      dirty={isDirty(m.master_key)}
                      disabled={!canDecide || busy}
                      catalogPrice={m.catalog_min}
                      platformPrice={m.rows.length > 0 && m.rows[0].platform_price !== null
                        ? Number(m.rows[0].platform_price) : null}
                      sized={m.sized}
                      onChange={onDraftChange}
                      compact
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-line flex flex-wrap items-center gap-3">
          <span className="text-xs text-t3">
            Θα δημιουργηθούν <strong className="text-t1">{preview.willSend}</strong>{' '}
            {preview.willSend === 1 ? 'πρόχειρη αλλαγή' : 'πρόχειρες αλλαγές'} σε{' '}
            <strong className="text-t1">{preview.stores}</strong>{' '}
            {preview.stores === 1 ? 'κατάστημα' : 'καταστήματα'}
            {preview.kept > 0 && (
              <span className="text-sky-700 dark:text-sky-300">
                {' '}· {preview.kept} είδη παραλείπονται («κράτα την τιμή efood»)
              </span>
            )}
            {preview.fixed > 0 && <span> · {preview.fixed} με σταθερή τιμή</span>}
            {dirtyCount > 0 && (
              <span className="text-blue-700 dark:text-blue-300"> · {dirtyCount} νέες αποφάσεις θα αποθηκευτούν πρώτα</span>
            )}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl text-sm"
            >
              Άκυρο
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy || masters.length === 0}
              title="Αποθηκεύει τις αποφάσεις και ανοίγει τη στεγνή δοκιμή — καμία εξερχόμενη κλήση"
              className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              Συγχρονισμός & στεγνή δοκιμή
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceSyncModal;
